import {
  Box,
  IconButton,
  Link as MuiLink,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';
import Alert from '@material-ui/lab/Alert';
import { BAYER_CYAN, BAYER_NAVY } from '../../theme/bayerTheme';

const LIVE_PORTAL_URL = 'https://backstage.int.bayer.com';

const STAGING_BANNER_DISMISS_KEY = 'topnav-staging-banner-dismissed';

const useRebrandStyles = makeStyles(theme => ({
  rebrandNotice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.35),
    minWidth: 0,
    maxWidth: '100%',
  },
  rebrandText: {
    color: theme.palette.common.white,
    fontSize: '0.78rem',
    fontWeight: 500,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rebrandEmphasis: {
    fontWeight: 700,
  },
  celebrationIcon: {
    color: BAYER_CYAN,
    fontSize: '1rem',
    flexShrink: 0,
  },
  trailingCelebrationIcon: {
    color: BAYER_CYAN,
    fontSize: '1rem',
    flexShrink: 0,
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },
}));

export const TopNavRebrandNotice = () => {
  const classes = useRebrandStyles();

  return (
    <div className={classes.rebrandNotice}>
      <CelebrationIcon className={classes.celebrationIcon} aria-hidden />
      <Typography component="span" className={classes.rebrandText}>
        Our developer portal has been rebranded from{' '}
        <Box component="strong" className={classes.rebrandEmphasis}>
          Backstage
        </Box>{' '}
        to{' '}
        <Box component="strong" className={classes.rebrandEmphasis}>
          IDP
        </Box>        
      </Typography>
      <CelebrationIcon className={classes.celebrationIcon} aria-hidden />
    </div>
  );
};

const useStagingStyles = makeStyles(theme => ({
  stagingSnackbar: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: -15,
    zIndex: theme.zIndex.snackbar,
    width: 'calc(100% - 32px)',
    maxWidth: 640,
    [theme.breakpoints.down('sm')]: {
      bottom: -8,
    },
  },
  stagingAlert: {
    backgroundColor: '#FF9800',
    color: '#000000',
    fontSize: '0.75rem',
    fontWeight: 500,
    padding: theme.spacing(0.25, 1),
    alignItems: 'center',
    boxShadow: theme.shadows[3],
    '& .MuiAlert-message': {
      padding: theme.spacing(0.25, 0),
      minWidth: 0,
      overflowWrap: 'anywhere',
    },
    '& .MuiAlert-action': {
      paddingTop: 0,
      marginRight: 0,
    },
  },
  stagingLink: {
    color: BAYER_NAVY,
    fontWeight: 600,
    textDecoration: 'underline',
    marginLeft: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      display: 'inline',
      marginLeft: theme.spacing(0.25),
    },
  },
}));

interface TopNavStagingSnackbarProps {
  open: boolean;
  onClose: () => void;
}

export const TopNavStagingSnackbar = ({
  open,
  onClose,
}: TopNavStagingSnackbarProps) => {
  const classes = useStagingStyles();

  if (!open) {
    return null;
  }

  return (
    <div
      className={classes.stagingSnackbar}
      role="status"
      aria-live="polite"
    >
      <Alert
        variant="filled"
        severity="warning"
        className={classes.stagingAlert}
        icon={false}
        action={
          <IconButton
            size="small"
            aria-label="Dismiss staging notice"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Typography component="span" variant="body2">
          You are viewing the staging environment, used for testing.
          <MuiLink
            href={LIVE_PORTAL_URL}
            className={classes.stagingLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            View the live portal
          </MuiLink>
        </Typography>
      </Alert>
    </div>
  );
};

export function readStagingBannerDismissed(): boolean {
  try {
    return sessionStorage.getItem(STAGING_BANNER_DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

export function dismissStagingBanner(): void {
  try {
    sessionStorage.setItem(STAGING_BANNER_DISMISS_KEY, 'true');
  } catch {
    // sessionStorage may be unavailable; ignore
  }
}
